import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useMemo, useState } from 'react';

import { AddCircle, Storage } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import { CreateEnvironmentCommand, CreateEnvironmentCommandResult, EnvironmentClient, ServiceConnectionClient } from '../../api/api';
import CloudProviderSelection from './Components/CloudProviderSelection';
import { CLOUD_PROVIDER_DEFINITIONS, CLOUD_PROVIDER_TYPES, CloudProviderType, getCloudProviderDefinition } from './Components/Consts';

interface CreateEnvironmentDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess: (result: CreateEnvironmentCommandResult) => void | Promise<void>;
	defaultOrder?: number;
	defaultCloudProvider?: CloudProviderType | '';
	onCloudProviderChange?: (provider: CloudProviderType) => void;
	title?: string;
	initialMetadata?: CreateEnvironmentCommand['metadata'];
}

export default function CreateEnvironmentDialog({
	open,
	setOpen,
	onSuccess,
	defaultOrder = 1,
	defaultCloudProvider = '',
	onCloudProviderChange,
	title = 'Create New Environment',
	initialMetadata,
}: CreateEnvironmentDialogProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const serviceConnectionClient = useApiClient(ServiceConnectionClient) as ServiceConnectionClient;
	const toastService = useToastService();

	const [formData, setFormData] = useState<CreateEnvironmentCommand>({
		name: '',
		code: '',
		order: defaultOrder,
		tags: [],
		serviceConnectionId: '',
		deploymentServiceConnectionType: '',
		attributes: {},
		metadata: initialMetadata,
	});
	const [applicationDisplayNamePrefix, setApplicationDisplayNamePrefix] = useState('');
	const [resourceGroupName, setResourceGroupName] = useState('');
	const [region, setRegion] = useState('');
	const [tagInput, setTagInput] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selectedCloudProvider, setSelectedCloudProvider] = useState<CloudProviderType | ''>('');
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);
	const [isMaximized, setIsMaximized] = useState(false);

	// Query for Cloud Provider service connections
	const { data: cloudProviderConnections, isLoading: isLoadingCloudProviders } = useQuery({
		queryKey: ['cloudProviderConnections'],
		queryFn: async () => {
			// Get all service connections from "Cloud Providers" category
			const result = await serviceConnectionClient.search({ category: 'Cloud Providers' });
			return result.items;
		},
		enabled: open, // Only fetch when dialog is open
	});

	// Determine available cloud providers from connections
	const availableProviders = useMemo(() => {
		return cloudProviderConnections ? Array.from(new Set(cloudProviderConnections.map((conn) => conn.type))) : [];
	}, [cloudProviderConnections]);

	const availableCloudProviderDefinitions = useMemo(
		() => CLOUD_PROVIDER_DEFINITIONS.filter((provider) => availableProviders.includes(provider.type)),
		[availableProviders],
	);

	// Filtered connections based on selected cloud provider
	const filteredConnections = cloudProviderConnections?.filter((conn) => (selectedCloudProvider ? conn.type === selectedCloudProvider : false)) || [];

	// Handle tab change
	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setSelectedTabIndex(newValue);

		const newProvider = availableCloudProviderDefinitions[newValue];

		if (newProvider) {
			setSelectedCloudProvider(newProvider.type);
			onCloudProviderChange?.(newProvider.type);
			// Reset service connection when cloud provider changes
			setFormData((prev) => ({ ...prev, serviceConnectionId: '', deploymentServiceConnectionType: '' }));
			// Reset cloud-specific fields
			setApplicationDisplayNamePrefix('');
			setResourceGroupName('');
			setRegion('');
		}
	};

	// Update form order when defaultOrder changes or dialog opens
	useEffect(() => {
		if (open) {
			setFormData((prev) => ({
				...prev,
				order: defaultOrder,
				metadata: initialMetadata,
			}));
		}
	}, [open, defaultOrder, initialMetadata]);

	// Set initial cloud provider when connections are loaded
	useEffect(() => {
		if (availableCloudProviderDefinitions.length > 0 && !selectedCloudProvider) {
			// Prefer the previous selection when available, then Azure, then first available.
			const defaultProvider =
				availableCloudProviderDefinitions.find((provider) => provider.type === defaultCloudProvider) ||
				availableCloudProviderDefinitions.find((provider) => provider.type === CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION) ||
				availableCloudProviderDefinitions[0];
			setSelectedCloudProvider(defaultProvider.type);
			setSelectedTabIndex(availableCloudProviderDefinitions.indexOf(defaultProvider));
		}
	}, [availableCloudProviderDefinitions, defaultCloudProvider, selectedCloudProvider]);

	const handleClose = () => {
		if (!isSubmitting) {
			setOpen(false);
			// Reset form
			setFormData({
				name: '',
				code: '',
				order: defaultOrder,
				tags: [],
				serviceConnectionId: '',
				deploymentServiceConnectionType: '',
				attributes: {},
				metadata: initialMetadata,
			});
			setSelectedCloudProvider('');
			setSelectedTabIndex(0);
			setApplicationDisplayNamePrefix('');
			setResourceGroupName('');
			setRegion('');
			setTagInput('');
		}
	};

	const handleAddTag = () => {
		if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
			setFormData((prev) => ({
				...prev,
				tags: [...(prev.tags || []), tagInput.trim()],
			}));
			setTagInput('');
		}
	};

	const handleRemoveTag = (tagToRemove: string) => {
		setFormData((prev) => ({
			...prev,
			tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
		}));
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddTag();
		}
	};

	const handleSubmit = async () => {
		if (!formData.name.trim()) {
			toastService.error('Environment name is required');
			return;
		}

		if (!formData.code.trim()) {
			toastService.error('Environment code is required');
			return;
		}

		if (!selectedCloudProvider) {
			toastService.error('Cloud Provider is required');
			return;
		}

		if (!formData.serviceConnectionId.trim()) {
			toastService.error('Service Connection is required');
			return;
		}

		if (!formData.deploymentServiceConnectionType.trim()) {
			toastService.error('Deployment service connection type is required');
			return;
		}

		// Azure-specific validations
		if (selectedCloudProvider === CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION) {
			if (!applicationDisplayNamePrefix.trim()) {
				toastService.error('Application Display Name Prefix is required');
				return;
			}

			if (!resourceGroupName.trim()) {
				toastService.error('Resource Group Name is required');
				return;
			}

			if (!region.trim()) {
				toastService.error('Resource Group Region is required');
				return;
			}
		}

		setIsSubmitting(true);
		try {
			// Include cloud provider specific attributes
			let attributes = { ...formData.attributes };
			const providerDefinition = getCloudProviderDefinition(selectedCloudProvider);
			if (providerDefinition) {
				attributes = {
					...attributes,
					...providerDefinition.providerAttribute,
				};
			}

			if (selectedCloudProvider === CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION) {
				attributes = {
					...attributes,
					'Azure:ApplicationDisplayNamePrefix': applicationDisplayNamePrefix.trim(),
					'Azure:ResourceGroupName': resourceGroupName.trim(),
					'Azure:Region': region,
				};
			}

			const environmentData = {
				...formData,
				attributes,
			};

			const result = await client.create(environmentData);
			onCloudProviderChange?.(selectedCloudProvider);
			toastService.success(`Environment "${formData.name}" created successfully`);
			await onSuccess(result);
			handleClose();
		} catch (error) {
			console.error('Error creating environment:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create environment: ${errorMessage}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography component="span" variant="h6" sx={{ flex: 1 }}>
					{title}
				</Typography>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={handleClose}
					closeDisabled={isSubmitting}
				/>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ pt: 1 }}>
					{/* Basic Information Section */}
					<Box>
						<Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
							<Storage color="primary" />
							Environment Details
						</Typography>

						<Stack spacing={2}>
							<TextField
								label="Environment Name"
								value={formData.name}
								onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								fullWidth
								required
								placeholder="e.g., Development, Staging, Production"
							/>

							<TextField
								label="Environment Code"
								value={formData.code}
								onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
								fullWidth
								required
								placeholder="e.g., DEV, STG, PROD"
								helperText="A short code to identify this environment"
							/>

							<Box>
								<TextField
									label="Add Tag"
									value={tagInput}
									onChange={(e) => setTagInput(e.target.value)}
									onKeyPress={handleKeyPress}
									fullWidth
									placeholder="Enter a tag and press Enter"
									InputProps={{
										endAdornment: (
											<Button onClick={handleAddTag} disabled={!tagInput.trim()} size="small" startIcon={<AddCircle />}>
												Add
											</Button>
										),
									}}
								/>

								{formData.tags && formData.tags.length > 0 && (
									<Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
										{formData.tags.map((tag) => (
											<Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} size="small" variant="outlined" />
										))}
									</Box>
								)}
							</Box>
						</Stack>
					</Box>

					<Divider />

					<CloudProviderSelection
						isLoading={isLoadingCloudProviders}
						availableProviders={availableCloudProviderDefinitions}
						selectedProvider={selectedCloudProvider}
						selectedTabIndex={selectedTabIndex}
						serviceConnectionId={formData.serviceConnectionId}
						filteredConnections={filteredConnections}
						region={region}
						resourceGroupName={resourceGroupName}
						applicationDisplayNamePrefix={applicationDisplayNamePrefix}
						onTabChange={handleTabChange}
						onServiceConnectionChange={(serviceConnectionId) => {
							const selectedConnection = filteredConnections.find((connection) => connection.id === serviceConnectionId);
							setFormData((prev) => ({
								...prev,
								serviceConnectionId,
								deploymentServiceConnectionType: selectedConnection?.type || selectedCloudProvider || '',
							}));
						}}
						onRegionChange={setRegion}
						onResourceGroupNameChange={setResourceGroupName}
						onApplicationDisplayNamePrefixChange={setApplicationDisplayNamePrefix}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={
						isSubmitting ||
						!formData.name.trim() ||
						!formData.code.trim() ||
						!selectedCloudProvider ||
						!formData.serviceConnectionId.trim() ||
						// Azure-specific required fields
						(selectedCloudProvider === CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION &&
							(!applicationDisplayNamePrefix.trim() || !resourceGroupName.trim() || !region.trim()))
					}
				>
					{isSubmitting ? 'Creating...' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
